import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  selectGatingCatalog,
  selectGatingCatalogFetched,
  selectGatingCatalogLoading,
  selectGatingConfigurations,
  selectStoryboardDraft,
  selectStoryboardPages,
} from "src/redux/selectors/experiences/storyboardSettings";
import {
  GatingCatalogRecord,
  GatingConfigurationResponse,
} from "src/types/experiences/storyboardSettings";
import { STORYBOARD_TYPES } from "src/types/experiences/storyboardSettings/storyboard";
import { useGatingCatalogQuery } from "src/query/experiences/storyboardSettings/gating";
import {
  usePageDetailQuery,
  useStoryboardSectionsQuery,
} from "src/query/experiences/storyboardSettings/pages";
import { isInteractiveContentType } from "./constants";

import Card from "../card";
import AutoGatingRow from "./autoGatingRow";
import GatingRow from "./gatingRow";
import { canEdit } from "src/pages/experiences/storyboardSettings/utils/permissions";
import {
  createAutoGatingSeed,
  createGatingSeed,
  makeGatingUid,
} from "./constants";
import type { WebsiteSettingsActions } from "../index";
import styles from "./gatingTriggersCard.module.scss";
import { PAGE_WIDGET_TYPE } from "src/pages/experiences/constants/storyboard";

const CATALOG_PAGE_SIZE = 50;

interface GatingTriggersCardProps {
  actions: WebsiteSettingsActions;
  storyboardId: string;
}

const GatingTriggersCard: React.FC<GatingTriggersCardProps> = ({
  actions,
  storyboardId,
}) => {
  const gatings = useSelector(selectGatingConfigurations);
  const catalog = useSelector(selectGatingCatalog);
  const catalogLoading = useSelector(selectGatingCatalogLoading);
  const catalogFetched = useSelector(selectGatingCatalogFetched);
  const storyboardPages = useSelector(selectStoryboardPages);
  const draft = useSelector(selectStoryboardDraft);
  const editable = canEdit(draft?.role);

  const [catalogLimit, setCatalogLimit] = useState(CATALOG_PAGE_SIZE);
  const catalogQuery = useGatingCatalogQuery({
    storyboard: storyboardId,
    published: true,
    page: 0,
    limit: catalogLimit,
  });
  const totalCatalogRecords =
    catalogQuery.data?.totalRecordsCount ?? catalog.length;
  const hasMoreCatalog = catalog.length < totalCatalogRecords;
  const loadMoreCatalog = () => {
    if (catalogLoading) return;
    if (!hasMoreCatalog) return;
    setCatalogLimit((current) => current + CATALOG_PAGE_SIZE);
  };

  const storyboardType = draft?.type;
  const isFlipBook = storyboardType === STORYBOARD_TYPES.FLIP_BOOK;
  const showAutoGatingUi = !isFlipBook;

  const isSmartEnabled = useMemo(
    () => gatings.some((g) => g.smart && g.enabled),
    [gatings],
  );

  const isV2 = storyboardType === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2;
  const mainPageId = draft?.page?.id;
  const pageDetailQuery = usePageDetailQuery(
    isInteractiveContentType(storyboardType) && !isV2 ? mainPageId : undefined,
  );

  const sectionsQuery = useStoryboardSectionsQuery(isV2 ? storyboardId : undefined);

  const sectionCount = useMemo(() => {
    if (isV2) {
      const data = sectionsQuery.data;
      return (data?.records ?? data?.items ?? []).length;
    }
    const widgets = pageDetailQuery.data?.draft?.widgets ?? [];
    return widgets.filter((w) => w.type === PAGE_WIDGET_TYPE).length;
  }, [isV2, sectionsQuery.data, pageDetailQuery.data]);

  const sectionLoading =
    isInteractiveContentType(storyboardType) &&
    (isV2
      ? Boolean(storyboardId) && sectionsQuery.isLoading
      : Boolean(mainPageId) && pageDetailQuery.isLoading);

  const { autoGating, regularGatings } = useMemo(() => {
    let auto: GatingConfigurationResponse | null = null;
    const rest: GatingConfigurationResponse[] = [];
    for (const config of gatings) {
      if (!isFlipBook && config?.autoGating && !auto) {
        auto = config;
      } else {
        rest.push(config);
      }
    }
    return { autoGating: auto, regularGatings: rest };
  }, [gatings, isFlipBook]);

  const catalogOptions = useMemo(
    () =>
      (catalog as GatingCatalogRecord[]).map((g) => ({
        id: g.id,
        name: g.name,
        progressive: g.progressive,
      })),
    [catalog],
  );

  const progressiveCatalogOptions = useMemo(
    () =>
      (catalog as GatingCatalogRecord[])
        .filter(
          (g) =>
            g.progressive && (g.progressiveGatingClusters?.length ?? 0) > 1,
        )
        .map((g) => ({ id: g.id, name: g.name, progressive: g.progressive })),
    [catalog],
  );

  const pageOptions = useMemo(
    () =>
      [...storyboardPages]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((p) => ({
          id: p.id,
          name: p.name ?? "",
          entityType: p.entityType,
        })),
    [storyboardPages],
  );

  const handleAdd = () => {
    if (!editable) return;
    actions.addGatingConfig(createGatingSeed());
  };

  const handleEdit = (data: GatingConfigurationResponse, silent?: boolean) => {
    if (!editable) return;
    const key = data.id ?? data.uid;
    if (!key) return;
    actions.editGatingConfig(key, data, silent);
  };

  const handleAutoGatingEdit = (
    data: GatingConfigurationResponse,
    silent?: boolean,
  ) => {
    if (!editable) return;

    if (autoGating) {
      const key = autoGating.id ?? autoGating.uid;
      if (key) actions.editGatingConfig(key, data, silent);
      return;
    }
    actions.addGatingConfig(
      {
        ...createAutoGatingSeed(),
        ...data,
        uid: makeGatingUid(),
      },
      silent,
    );
  };

  const handleDelete = (key?: string) => {
    if (!editable) return;
    if (!key) return;
    actions.removeGatingConfig(key);
  };

  return (
    <Card
      title="Gating Triggers"
      description="Configure when and how visitors are prompted for their details"
    >
      {showAutoGatingUi && (
        <AutoGatingRow
          config={autoGating ?? undefined}
          editable={editable}
          onEdit={handleAutoGatingEdit}
          progressiveCatalogOptions={progressiveCatalogOptions}
          catalogLoading={catalogLoading}
          catalogFetched={catalogFetched}
          catalogHasMore={hasMoreCatalog}
          loadMoreCatalog={loadMoreCatalog}
        />
      )}

      {regularGatings.length > 0 && (
        <div className={styles.rowList}>
          {regularGatings.map((config, idx) => (
            <React.Fragment key={config.id ?? config.uid}>
              <div className={styles.divider} />
              <GatingRow
                config={config}
                storyboardType={storyboardType}
                catalogOptions={catalogOptions}
                pageOptions={pageOptions}
                sectionCount={sectionCount}
                sectionLoading={sectionLoading}
                isSmartEnabled={isSmartEnabled}
                onEdit={handleEdit}
                onDelete={() => handleDelete(config.id ?? config.uid)}
                catalogLoading={catalogLoading}
                catalogHasMore={hasMoreCatalog}
                loadMoreCatalog={loadMoreCatalog}
                editable={editable}
              />
              {idx === regularGatings.length - 1 && (
                <div className={styles.divider} />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <button
        className={styles.addButton}
        onClick={handleAdd}
        disabled={!editable}
      >
        <span className={styles.icon}>
          <i className="paperflite-add" />
        </span>
        <span className={styles.name}>new gating</span>
      </button>
    </Card>
  );
};

export default GatingTriggersCard;
